import { useState } from "react";
import {
  Button,
  Switch,
  TextField,
  Box,
  Paper,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import BorderColorRoundedIcon from "@mui/icons-material/BorderColorRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import DraftsRoundedIcon from "@mui/icons-material/DraftsRounded";
import {
  registerPatientButtonStyles,
  addButtonPadding,
} from "../../common/commonStyles";
import Grid from "@mui/material/Grid";
import "../../common/Common.css";
import { DataGrid } from "@mui/x-data-grid";
import { checkFormValidation } from "../../shared/helper";
import UserCreationForm from "./UserCreationForm";
import JoiningLetterDialog from "./JoiningLetterDialog";
import ReusableSnackbar from "../../shared/Alert/ReusableSnackbar";
import api from "../../services/api";

// ---- shared palette (indigo = primary action only, teal = status) ------
const palette = {
  indigo: "#4B49AC",
  indigoDark: "#37357D",
  ink: "#1F2937",
  slate: "#6B7280",
  line: "#E6E9ED",
  bg: "#FAFBFC",
  teal: "#12A594",
  tealSoft: "#DDF6F1",
  coral: "#FF6B57",
  coralSoft: "#FFEFEC",
};

const actionIconBtn = (color, hoverBg) => ({
  width: 30,
  height: 30,
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  color,
  transition: "background-color 120ms ease",
  "&:hover": { backgroundColor: hoverBg },
});

const ONBOARDING_ROLES = ["staff", "receptionist"];

export default function UserTable(props) {
  const { branchId, userDetails, getUsersDetail, roles } = props;
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    address: "",
    email: "",
    name: "",
    phone_number: "",
    role: "",
  });
  const [snackbarOpen, setSnackbarOpen] = useState({
    status: false,
    message: "",
    severity: "",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [userID, setUserID] = useState();
  const [isEditClicked, setIsEditClicked] = useState(false);

  // Joining letter dialog state
  const [letterDialogOpen, setLetterDialogOpen] = useState(false);
  const [letterUser, setLetterUser] = useState(null);

  const handleOpen = () => setOpen(true);

  const handleClose = (event, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    if (reason === "escapeKeyDown") {
      return;
    }
    setFormData({
      address: "",
      email: "",
      name: "",
      phone_number: "",
      role: "",
    });
    setOpen(false);
  };

  // FIX: was `setSnackbarOpen(false)`, which would throw the next time
  // snackbarOpen.status/message/severity was read. Update the object instead.
  const handleSnackClose = () =>
    setSnackbarOpen((prev) => ({ ...prev, status: false }));

  const requiredFields = ["address", "email", "name", "phone_number", "role"];
  const isFormValid = checkFormValidation(formData, requiredFields);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const filteredRows = userDetails.filter((row) =>
    Object.values(row).some(
      (val) =>
        val && val.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const handleOpenLetter = (row) => {
    setLetterUser(row);
    setLetterDialogOpen(true);
  };

  const handleCloseLetter = () => {
    setLetterDialogOpen(false);
    setLetterUser(null);
  };

  const handleLetterSent = () => {
    setLetterDialogOpen(false);
    setLetterUser(null);
    setSnackbarOpen({
      status: true,
      message: "Joining letter sent successfully!",
      severity: "success",
    });
    getUsersDetail();
  };

  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <span style={{ fontWeight: 600, color: palette.ink }}>
          {params.value}
        </span>
      ),
    },
    { field: "email", headerName: "Email", flex: 1.5, minWidth: 200 },
    { field: "phone_number", headerName: "Mobile", width: 140 },
    { field: "address", headerName: "Address", flex: 2, minWidth: 220 },
    { field: "role", headerName: "Role", width: 120 },
    {
      field: "is_active",
      headerName: "Is Active",
      width: 110,
      editable: false,
      sortable: false,
      renderCell: (param) => (
        <Switch
          defaultChecked={param.row.is_active}
          onChange={(event) => handleToggleIsActive(event, param.row)}
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": { color: palette.teal },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: palette.teal,
            },
          }}
        />
      ),
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      minWidth: 160,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        const isActive = params.row.is_active;
        const isOnboardingRole = ONBOARDING_ROLES.includes(params.row.role);

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "6px",
              width: "100%",
              height: "100%",
            }}
          >
            {isOnboardingRole && (
              <Tooltip title="Joining letter">
                <Box
                  sx={actionIconBtn(palette.indigo, palette.tealSoft)}
                  onClick={() => handleOpenLetter(params.row)}
                >
                  <DraftsRoundedIcon sx={{ fontSize: "17px" }} />
                </Box>
              </Tooltip>
            )}
            <Tooltip title={isActive ? "Edit user" : "User is inactive"}>
              <Box
                sx={actionIconBtn(
                  isActive ? palette.slate : "#CBD5E1",
                  "#F1F5F9",
                )}
                style={{ pointerEvents: isActive ? "auto" : "none" }}
                onClick={() => {
                  handleUserEdit(params.row.id);
                }}
              >
                <BorderColorRoundedIcon sx={{ fontSize: "17px" }} />
              </Box>
            </Tooltip>
            <Tooltip title="Delete user">
              <Box
                sx={actionIconBtn(palette.coral, palette.coralSoft)}
                onClick={() => {
                  // handleServiceDelete(params.row.id);
                }}
              >
                <DeleteRoundedIcon sx={{ fontSize: "17px" }} />
              </Box>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  const handleToggleIsActive = async (event, row) => {
    const newValue = event.target.checked;
    try {
      await api.patch(`app/management/users/${row.id}`, {
        is_active: newValue,
      });
      if (typeof getUsersDetail === "function") {
        getUsersDetail(); // parent should refetch and pass updated `rows` again
      }
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  const handleCreateUser = async () => {
    if (!isFormValid) {
      setSnackbarOpen({
        status: true,
        message: "Please fill all required fields correctly.",
        severity: "error",
      });
      return;
    }
    if (isEditClicked) {
      sendUserEdit();
    } else {
      try {
        const response = await api.post(
          `app/management/users/${branchId}`,
          formData
        );

        if (response.status === 200) {
          setFormData({
            address: "",
            email: "",
            name: "",
            phone_number: "",
            role: "",
          });
          setSnackbarOpen({
            status: true,
            message: response?.data?.requires_joining_letter
              ? "User created. They're inactive until a joining letter is sent."
              : "User created successfully with default password!",
            severity: "success",
          });
          handleClose();
          getUsersDetail();
        }
      } catch (error) {
        setSnackbarOpen({
          status: true,
          message:
            error?.response?.data?.detail?.[0]?.msg ||
            error?.response?.data?.detail ||
            "Something went wrong ! Please try again",
          severity: "error",
        });
      }
    }
  };
  const handleUserEdit = async (id) => {
    setUserID(id);
    setIsEditClicked(true);
    await getDetailsUserWise(id);
    handleOpen();
  };
  const getDetailsUserWise = async (id) => {
    try {
      let userData = await api.get(`app/management/users/?user_id=${id}`);
      if (userData?.status === 200) {
        setFormData({
          address: userData?.data[0]?.address,
          email: userData?.data[0]?.email,
          name: userData?.data[0]?.name,
          phone_number: userData?.data[0]?.phone_number,
          role: userData?.data[0]?.role,
        });
      } else {
        setSnackbarOpen({
          status: true,
          message: "Something went wrong. Please try again later.",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbarOpen({
        status: true,
        message: "Something went wrong. Please try again later.",
        severity: "error",
      });
    }
  };
  const sendUserEdit = async () => {
    setIsEditClicked(true);
    try {
      let updatedUserData = await api.patch(
        `app/management/users/${userID}`,
        formData
      );
      if (updatedUserData?.status === 200) {
        setFormData({
          address: "",
          email: "",
          name: "",
          phone_number: "",
          role: "",
        });
        setSnackbarOpen({
          status: true,
          message: "User updated successfully!",
          severity: "success",
        });
        handleClose();
        getUsersDetail();
      }
    } catch (error) {
      setSnackbarOpen({
        status: true,
        message: "Something went wrong. Please try again later.",
        severity: "error",
      });
    }
  };

  return (
    <>
      <Box className="tabcontent_box" sx={{ backgroundColor: palette.bg }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: "14px",
            border: `1px solid ${palette.line}`,
            padding: "16px 20px",
            marginBottom: "16px",
            backgroundColor: "#fff",
          }}
        >
          <Grid
            container
            spacing={2}
            className="patientTable-btnstack"
            display="flex"
            alignItems="center"
          >
            <Grid size={8} sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Button
                variant="contained"
                startIcon={<PersonAddAlt1RoundedIcon sx={{ fontSize: "18px" }} />}
                sx={[
                  registerPatientButtonStyles,
                  addButtonPadding,
                  {
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "10px",
                    backgroundColor: palette.indigo,
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: palette.indigoDark,
                      boxShadow: "none",
                    },
                  },
                ]}
                size="small"
                onClick={handleOpen}
              >
                Add New User
              </Button>
              <span style={{ fontSize: "13px", color: palette.slate }}>
                {filteredRows?.length ?? 0} user
                {filteredRows?.length === 1 ? "" : "s"}
              </span>
            </Grid>
            <Grid size={4} sx={{ textAlign: "right" }}>
              <TextField
                size="small"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  width: "100%",
                  maxWidth: "280px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    backgroundColor: palette.bg,
                    fontSize: "13.5px",
                    "& fieldset": { borderColor: palette.line },
                    "&:hover fieldset": { borderColor: palette.slate },
                    "&.Mui-focused fieldset": { borderColor: palette.indigo },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ fontSize: "18px", color: palette.slate }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: "14px",
            border: `1px solid ${palette.line}`,
            overflow: "hidden",
          }}
        >
          <Box sx={{ height: 400, width: "100%" }}>
            <DataGrid
              sx={{
                fontFamily: "Nunito, sans-serif",
                border: "none",
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: palette.bg,
                  borderBottom: `1px solid ${palette.line}`,
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "12.5px",
                  fontWeight: 700,
                  color: palette.slate,
                  textTransform: "uppercase",
                  letterSpacing: "0.03em",
                },
                "& .MuiDataGrid-cell": {
                  fontSize: "13px",
                  borderBottom: `1px solid ${palette.line}`,
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#F5F6F8",
                },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
                "& .MuiDataGrid-footerContainer": {
                  borderTop: `1px solid ${palette.line}`,
                },
              }}
              rows={filteredRows}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 5,
                  },
                },
              }}
              pageSizeOptions={[5]}
              disableRowSelectionOnClick
            />
          </Box>
        </Paper>
      </Box>
      <UserCreationForm
        open={open}
        handleOpen={handleOpen}
        handleClose={handleClose}
        formData={formData}
        setFormData={setFormData}
        handleInputChange={handleInputChange}
        handleCreateUser={handleCreateUser}
        isFormValid={isFormValid}
        isEditClicked={isEditClicked}
        roles={roles}
      />
      <JoiningLetterDialog
        open={letterDialogOpen}
        user={letterUser}
        onClose={handleCloseLetter}
        onSent={handleLetterSent}
      />
      <ReusableSnackbar
        open={snackbarOpen.status}
        message={snackbarOpen.message}
        onClose={handleSnackClose}
        severity={snackbarOpen.severity}
      />
    </>
  );
}